import { Calculator } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { CommonContainer } from "../../../components";

import { useGetReviewData } from "../../../api/ingredients";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ExternalWarehouse from "./ExternalStorage/ExternalWarehouse";
import ExternalTransport from "./ExternalTransport/ExternalTransport";
import PackagingTable from "./Packaging/Packaging";

import { useGetDrawingToolData, usePutDrawingToolDataToQueue } from "../../../api/drawingTool";
import { useGetFertilizerData, useGetPesticideData } from "../../../api/static-data";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import useUploadMappingImage from "../../../hooks/useUploadMappingImage";
import CultivationTable from "./Cultivation/Cultivation";
import Ingredient from "./Ingredient/Ingredient";
import Process from "./Process/Process";
import ProcessFarmGateStorage from "./ProcessFarmGateStorage/ProcessFarmGateStorage";
import ProcessFarmGateTransport from "./ProcessFarmGateTransport/ProcessFarmGateTransport";

import "./mappingDataView.scss";

const MappingDataReview = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { drawingId, ownerId, sourceId } = useParams();
  const { mappingType: paramMappingType } = useParams<string>();
  const mappingType = paramMappingType as keyof typeof MappingSourceType;
  const mappingSourceType = MappingSourceType[mappingType];
  const [calculateConfirm, setCalculateConfirm] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const isAverageProduct = queryParams.get("isAverageProduct") === "true";
  const isEditable = queryParams.get("isEditable") === "true";

  const { data: fertilizerData } = useGetFertilizerData();
  const { data: pesticideData, status, fetchStatus } = useGetPesticideData();
  const { isLoading: isLoadingPutDataToQueue, mutate: putDrawingToolDataToQueueAsync } =
    usePutDrawingToolDataToQueue(mappingSourceType);
  const { data: drawingToolData, isLoading: isDrawingToolLoading } = useGetDrawingToolData(
    mappingSourceType,
    sourceId,
    ownerId
  );
  const [isLoadingImageUpload, uploadImage] = useUploadMappingImage(
    drawingToolData?.chartNodes || [],
    mappingSourceType
  );

  const uploadMappingImage = () => {
    if (!sourceId || !ownerId) return;
    switch (mappingSourceType) {
      case MappingSourceType.ingredient:
        return uploadImage(sourceId, ownerId, () => {});
      default:
        uploadImage(sourceId, ownerId, () => {});
    }
  };

  const navigateAndUploadImage = () => {
    uploadMappingImage();
    navigate(
      `/product-summary?salesUnitId=${sourceId}&productId=${ownerId}&isAverageProduct=${isAverageProduct}`
    );
  };

  const calculateHandleApprove = () => {
    putDrawingToolDataToQueueAsync(drawingToolData, {
      onError: error => {
        cancel();
      },
      onSuccess: () => {
        navigateAndUploadImage();
      }
    });
  };

  const {
    data: reviewData,
    isSuccess,
    refetch
  } = useGetReviewData({
    drawingId: drawingId
  });

  const cancel = () => {
    return navigate(`/mapping/${mappingSourceType}?sourceId=${sourceId}`);
  };

  if (
    (status == "loading" && fetchStatus == "fetching") ||
    isDrawingToolLoading ||
    isLoadingImageUpload ||
    isLoadingPutDataToQueue
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <CommonSplitContainer
      currentHeight={0.99}
      topChild={
        <>
          <Grid>
            <Grid.Column className="mt-1 mappingDataViewMain">
              <MainBottomMultipleButtonView
                customBottomContentMain="mainBottomMultipleButtonViewCustom"
                leftButtons={[]}
                rightButtons={[
                  {
                    contentView: 16,
                    title: "Back",
                    onClick: () => cancel(),
                    buttonView: true,
                    buttonOutLine: true
                  }
                ]}
              />
              <CommonContainer>
                <Ingredient IngredientData={reviewData?.ingredients} />
                <CultivationTable
                  reviewData={reviewData}
                  fertilizerData={fertilizerData}
                  pesticideData={pesticideData}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <Process
                  reviewData={reviewData}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <PackagingTable
                  reviewData={reviewData}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <ProcessFarmGateStorage
                  reviewData={reviewData}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <ProcessFarmGateTransport
                  reviewData={reviewData}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <ExternalWarehouse
                  externalStorage={reviewData?.externalStorage || []}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <ExternalTransport
                  externalTransport={reviewData?.externalTransportation || []}
                  isEditable={isEditable}
                  refetch={() => refetch()}
                />
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Back",
                      onClick: () => cancel(),
                      buttonOutLine: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 16,
                      title: "Calculate",
                      onClick: () => setCalculateConfirm(true),
                      buttonView: true
                    }
                  ]}
                />
              </CommonContainer>
            </Grid.Column>
          </Grid>
          <ConfirmModal
            viewModal={calculateConfirm}
            closeModal={() => setCalculateConfirm(false)}
            cancel={() => {
              setCalculateConfirm(false);
            }}
            approve={() => {
              setCalculateConfirm(false);
              calculateHandleApprove();
            }}
            title={"Calculation Mapping Data"}
            subTitle="Are you sure you have entered all the required data for calculations? Please check the data again or contact Nature Preserve."
            variant="default"
            confirmText="Calculate"
            icon={<Calculator className="tw-h-5 tw-w-5" />}
          />
        </>
      }
    />
  );
};

export default MappingDataReview;
